/**
 * Global Auto-Refresh System v2.0
 * 
 * Enterprise-grade auto-refresh system that eliminates UI lag across the entire app.
 * Every mutation automatically triggers instant UI updates without manual refresh.
 * 
 * Features:
 * - Automatic data refetch after any mutation
 * - Optimistic updates for instant feedback
 * - Zero-lag UI with smart loading states
 * - Resource-based event routing
 * - Debounced refetch to prevent spam
 * 
 * Usage:
 *   import { useAutoRefresh, triggerGlobalRefresh } from '@/lib/autoRefresh';
 *   
 *   // In any component:
 *   const { data, loading, refetch } = useAutoRefresh('/crm/', []);
 *   
 *   // After mutation:
 *   await api.post('/api/crm/', newData);
 *   triggerGlobalRefresh('crm'); // All components listening to 'crm' auto-refresh
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './api';

// Global event bus for cross-component refresh
const refreshBus = new EventTarget();

// Active listeners tracking (for debugging)
let activeListeners = 0;

/**
 * Trigger a global refresh for a specific resource type
 * This will cause ALL components using useAutoRefresh with this resource to refetch instantly
 */
export function triggerGlobalRefresh(resourceType) {
  if (!resourceType) {
    console.warn('[AutoRefresh] triggerGlobalRefresh called with no resourceType');
    return;
  }
  
  console.log(`[AutoRefresh] 🔄 Triggering refresh for: ${resourceType}`);
  
  refreshBus.dispatchEvent(new CustomEvent('gio:data-changed', { 
    detail: { resourceType, timestamp: Date.now() }
  }));
  
  // Also dispatch to window for backward compatibility
  window.dispatchEvent(new CustomEvent('gio:data-changed', { 
    detail: { resourceType, timestamp: Date.now() }
  }));
}

/**
 * Trigger multiple resource refreshes at once
 */
export function triggerMultipleRefresh(resourceTypes) {
  if (!Array.isArray(resourceTypes)) {
    console.warn('[AutoRefresh] triggerMultipleRefresh expects array');
    return;
  }
  resourceTypes.forEach(type => triggerGlobalRefresh(type));
}

/**
 * Hook for automatic data fetching with instant auto-refresh on mutations
 * 
 * @param {string} endpoint - API endpoint to fetch from
 * @param {any} initialData - Initial data state
 * @param {string|string[]|null} refreshOn - Resource type(s) to listen for refresh events (null = no auto-refresh)
 * @param {object} options - Additional options
 * @returns {object} { data, setData, loading, error, refetch, mutate, isRefreshing }
 */
export function useAutoRefresh(endpoint, initialData, refreshOn = null, options = {}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const fetchingRef = useRef(false);
  const lastFetchRef = useRef(0);
  const fetchTimeoutRef = useRef(null);

  const {
    transform = (d) => d,     // Transform response data before setting state
    fetchOnMount = true,       // Auto-fetch on component mount
    debounce = 100,            // Debounce time for rapid refreshes (ms)
    minInterval = 1000,        // Minimum time between fetches (ms)
    silent = false,            // Silent mode - no loading states
    onSuccess = null,          // Callback on successful fetch
    onError = null,            // Callback on fetch error
  } = options;

  // Fetch data from API
  const refetch = useCallback(async (opts = {}) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;
    
    // Prevent duplicate fetches
    if (fetchingRef.current && !opts.force) {
      console.log(`[AutoRefresh] ${endpoint} - fetch already in progress, skipping`);
      return;
    }
    
    // Enforce minimum interval between fetches (can be overridden with force)
    if (timeSinceLastFetch < minInterval && !opts.force) {
      console.log(`[AutoRefresh] ${endpoint} - too soon (${timeSinceLastFetch}ms), skipping`);
      return;
    }
    
    fetchingRef.current = true;
    lastFetchRef.current = now;
    
    // Safety timeout: reset fetchingRef after 10 seconds if still stuck
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      if (fetchingRef.current) {
        console.warn(`[AutoRefresh] ${endpoint} - fetch timeout, resetting fetchingRef`);
        fetchingRef.current = false;
      }
    }, 10000);
    
    if (!silent && !opts.background) {
      setLoading(true);
    }
    
    if (opts.background) {
      setIsRefreshing(true);
    }
    
    setError(null);

    try {
      console.log(`[AutoRefresh] ${endpoint} - fetching data...`);
      const response = await api.get(endpoint);
      console.log(`[AutoRefresh] ${endpoint} - response received:`, response.data);
      const transformedData = transform(response.data);
      console.log(`[AutoRefresh] ${endpoint} - data transformed:`, transformedData);
      
      if (mountedRef.current) {
        setData(transformedData);
        if (onSuccess) onSuccess(transformedData);
        console.log(`[AutoRefresh] ${endpoint} - data refreshed successfully, state updated`);
      } else {
        console.warn(`[AutoRefresh] ${endpoint} - component unmounted, skipping state update`);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        if (onError) onError(err);
        console.error(`[AutoRefresh] ${endpoint} - fetch failed:`, err);
      }
    } finally {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      if (mountedRef.current) {
        setLoading(false);
        setIsRefreshing(false);
      }
      fetchingRef.current = false;
      console.log(`[AutoRefresh] ${endpoint} - fetch complete, fetchingRef reset`);
    }
  }, [endpoint, transform, silent, minInterval, onSuccess, onError]);

  // Debounced refetch to prevent rapid consecutive calls
  const debouncedRefetch = useCallback((opts = {}) => {
    const timer = setTimeout(() => refetch({ ...opts, background: true }), debounce);
    return () => clearTimeout(timer);
  }, [refetch, debounce]);

  // Initial fetch on mount
  useEffect(() => {
    if (fetchOnMount) {
      console.log(`[AutoRefresh] ${endpoint} - initial fetch on mount`);
      refetch({ force: true }); // Force initial fetch to bypass any locks
    }
    // Don't set mountedRef to false here - it causes issues with React 18 Strict Mode
    // The mountedRef will be set to false only on final component unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
    // Only run on mount - refetch is stable via useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Track component mount/unmount separately
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      console.log(`[AutoRefresh] ${endpoint} - component unmounting`);
    };
  }, [endpoint]);

  // Listen for refresh events from refreshBus
  useEffect(() => {
    if (!refreshOn) return;

    const resourceTypes = Array.isArray(refreshOn) ? refreshOn : [refreshOn];
    
    const handleRefresh = (event) => {
      const { resourceType } = event.detail || {};
      
      if (!resourceType) return;
      
      // Match specific resource or wildcard '*'
      if (resourceTypes.includes(resourceType) || resourceTypes.includes('*') || resourceType === '*') {
        console.log(`[AutoRefresh] ${endpoint} - received refresh signal for ${resourceType}`);
        debouncedRefetch();
      }
    };

    // Listen on both refreshBus and window for maximum compatibility
    refreshBus.addEventListener('gio:data-changed', handleRefresh);
    window.addEventListener('gio:data-changed', handleRefresh);
    
    activeListeners++;
    console.log(`[AutoRefresh] ${endpoint} - listening for [${resourceTypes.join(', ')}] (total listeners: ${activeListeners})`);
    
    return () => {
      refreshBus.removeEventListener('gio:data-changed', handleRefresh);
      window.removeEventListener('gio:data-changed', handleRefresh);
      activeListeners--;
    };
  }, [refreshOn, debouncedRefetch, endpoint]);

  /**
   * Optimistic mutation helper
   * Updates local state immediately, then performs API call
   * Rolls back on error for seamless UX
   */
  const mutate = useCallback(async (mutationFn, optimisticData) => {
    const previousData = data;
    
    // Optimistic update - instant feedback
    if (optimisticData !== undefined) {
      setData(optimisticData);
      console.log(`[AutoRefresh] ${endpoint} - optimistic update applied`);
    }

    try {
      const result = await mutationFn();
      // Refetch to get server truth (background refresh - no loading state)
      await refetch({ background: true, force: true });
      return result;
    } catch (err) {
      // Rollback on error
      if (optimisticData !== undefined) {
        setData(previousData);
        console.log(`[AutoRefresh] ${endpoint} - rollback on error`);
      }
      throw err;
    }
  }, [data, refetch, endpoint]);

  return { 
    data, 
    setData, 
    loading, 
    error, 
    refetch, 
    mutate, 
    isRefreshing 
  };
}

/**
 * Hook for mutations with automatic global refresh
 * 
 * @param {string|string[]} refreshResources - Resource type(s) to refresh after mutation
 * @returns {function} mutationFn - Wrapped mutation function
 */
export function useMutation(refreshResources) {
  return useCallback(async (mutationFn) => {
    try {
      const result = await mutationFn();
      
      // Trigger global refresh for specified resources
      if (refreshResources) {
        const resources = Array.isArray(refreshResources) ? refreshResources : [refreshResources];
        console.log(`[AutoRefresh] Mutation complete - triggering refresh for:`, resources);
        triggerMultipleRefresh(resources);
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  }, [refreshResources]);
}

/**
 * Force immediate re-render helper
 * Use when React's batching is preventing immediate updates
 */
export function forceUpdate(setState) {
  setState(prev => ({ ...prev }));
}

/**
 * Get active listeners count (for debugging)
 */
export function getActiveListenersCount() {
  return activeListeners;
}

// Export aliases for backward compatibility
export const triggerRefresh = triggerGlobalRefresh;
export { triggerGlobalRefresh as default };
